import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import { completeWorkOSDashboardCallback } from "@/lib/workos-callback";
import { resetImpersonationCache } from "@/lib/ui-cache";

function CallbackStatus({
  title,
  detail,
  failed = false,
}: {
  title: string;
  detail: string;
  failed?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{detail}</p>
        {failed && (
          <a
            className="inline-flex rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
            href="/"
          >
            {t("auth.returnToApplication")}
          </a>
        )}
      </div>
    </main>
  );
}

/** Normal app-initiated callbacks remain owned by AuthKit's PKCE flow. */
export function WorkOSCallbackPage() {
  const { t } = useTranslation();
  const { isLoading, user } = useAuthWithTestMode();

  useEffect(() => {
    if (!isLoading && user) {
      window.location.replace("/");
    }
  }, [isLoading, user]);

  if (!isLoading && !user) {
    return (
      <CallbackStatus
        failed
        title={t("auth.signInFailed")}
        detail={t("auth.signInFailedDescription")}
      />
    );
  }

  return (
    <CallbackStatus
      title={t("auth.completingSignIn")}
      detail={t("auth.preparingSession")}
    />
  );
}

/**
 * Standalone callback used before AuthKitProvider mounts. This prevents the
 * browser SDK from deleting a dashboard impersonation code merely because no
 * app-created PKCE verifier exists.
 */
export function WorkOSDashboardCallbackPage() {
  const { t } = useTranslation();
  const startedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) {
      setError(t("auth.missingAuthorizationCode"));
      return;
    }

    void (async () => {
      try {
        await resetImpersonationCache(true, true);
      } catch {
        setError(t("auth.cacheResetFailed"));
        return;
      }
      try {
        await completeWorkOSDashboardCallback({
          code,
          clientId: import.meta.env.WORKOS_CLIENT_ID ?? "",
        });
        // A full navigation is intentional: AuthKit reads the newly installed
        // refresh token on initialization and hydrates all in-memory state,
        // including the impersonated user and impersonator details.
        window.location.replace("/");
      } catch (cause) {
        console.error("[auth] WorkOS impersonation callback failed", cause);
        setError(cause instanceof Error ? cause.message : t("auth.impersonationFailed"));
      }
    })();
  }, [t]);

  if (error) {
    return (
      <CallbackStatus
        failed
        title={t("auth.impersonationStartFailed")}
        detail={t("auth.impersonationStartFailedDescription", { error })}
      />
    );
  }

  return (
    <CallbackStatus
      title={t("auth.startingImpersonation")}
      detail={t("auth.preparingImpersonatedSession")}
    />
  );
}
